import { useEffect, useMemo, useState } from 'react';

import { STICKY_PAGE_ACTIONS_EVENTS } from '../constants';
import {
    ActionItem,
    RegisterEventData,
    RemoveEventData,
    ToggleVisibleEventData,
    UpdateBadgeCountEventData,
    UpdateBottomOffsetEventData,
} from '../model';

export const useActionsData = () => {
    const [actions, setActions] = useState<ActionItem[]>([]);
    const [bottomOffset, setBottomOffset] = useState<string>('0rem');

    const registerActionHandler = (event: CustomEvent<RegisterEventData>) => {
        const { action } = event.detail;
        setActions((currentActions) =>
            currentActions.find(({ id }) => id === action.id)
                ? currentActions.map((currentAction) => (currentAction.id === action.id ? action : currentAction))
                : [...currentActions, action],
        );
    };

    const removeActionHandler = (event: CustomEvent<RemoveEventData>) => {
        const { actionId } = event.detail;
        setActions((currentActions) => currentActions.filter(({ id }) => id !== actionId));
    };

    const toggleVisibleHandler = (event: CustomEvent<ToggleVisibleEventData>) => {
        const { actionId, isVisible } = event.detail;
        setActions((currentActions) =>
            currentActions.map((action) =>
                action.id === actionId && action.isVisible !== isVisible ? { ...action, isVisible } : action,
            ),
        );
    };

    const updateBadgeCountHandler = (event: CustomEvent<UpdateBadgeCountEventData>) => {
        const { actionId, count: badgeCount } = event.detail;
        setActions((currentActions) =>
            currentActions.map((action) => (action.id === actionId ? { ...action, badgeCount } : action)),
        );
    };

    const updateBottomOffsetHandler = (event: CustomEvent<UpdateBottomOffsetEventData>) => {
        const { offset } = event.detail;
        setBottomOffset(offset);
    };

    useEffect(() => {
        window.addEventListener(STICKY_PAGE_ACTIONS_EVENTS.REGISTER_ACTION, registerActionHandler);
        window.addEventListener(STICKY_PAGE_ACTIONS_EVENTS.REMOVE_ACTION, removeActionHandler);
        window.addEventListener(STICKY_PAGE_ACTIONS_EVENTS.TOGGLE_VISIBLE, toggleVisibleHandler);
        window.addEventListener(STICKY_PAGE_ACTIONS_EVENTS.UPDATE_BADGE_COUNT, updateBadgeCountHandler);
        window.addEventListener(STICKY_PAGE_ACTIONS_EVENTS.UPDATE_BOTTOM_OFFSET, updateBottomOffsetHandler);

        return () => {
            window.removeEventListener(STICKY_PAGE_ACTIONS_EVENTS.REGISTER_ACTION, registerActionHandler);
            window.removeEventListener(STICKY_PAGE_ACTIONS_EVENTS.REMOVE_ACTION, removeActionHandler);
            window.removeEventListener(STICKY_PAGE_ACTIONS_EVENTS.TOGGLE_VISIBLE, toggleVisibleHandler);
            window.removeEventListener(STICKY_PAGE_ACTIONS_EVENTS.UPDATE_BADGE_COUNT, updateBadgeCountHandler);
            window.removeEventListener(STICKY_PAGE_ACTIONS_EVENTS.UPDATE_BOTTOM_OFFSET, updateBottomOffsetHandler);
        };
    }, []);

    const sortedActions = useMemo(() => actions.sort((action1, action2) => action1.index - action2.index), [actions]);

    return { actions: sortedActions, bottomOffset };
};
